/**
 * Protección frente al clima de un anuncio (lluvia, frío, sol). Ver docs/weather-protection.md.
 *
 * Una tarjeta por clima con un interruptor: encendido = apto para ese clima. Las opciones son
 * **una sola lista compartida**: cada tarjeta enseña las que cubren su clima, y marcar "Espacio
 * interior" en Lluvia lo deja marcado en Frío y Sol (sin encenderlos). Encendido, hay que tener
 * al menos una ("Otro" incluido). Por clima: el interruptor, para cuántas personas es apto y el
 * texto de "Otro" (que no se comparte: no sabemos qué clima cubre).
 *
 * En `publicData`:
 * - `weatherProtection` (objeto, el detalle), solo con los climas activos:
 *   `{ rain: { protections, capacity, other? }, cold: {…}, sun: {…} }`. No se registra en Console.
 * - `weatherProtectedFor` (array plano, con índice de búsqueda): los climas activos.
 *
 * No toca `hasIndoorSpace` ni alimenta el chip "Con espacio interior" (que mira ese campo y las
 * extras `comedor` / `salon`): marcar "Espacio interior" aquí no activa nada de eso.
 */

import { parseGuestTierBoundsFromCapacityKey, resolveSavedCapacity } from './capacityFieldUtils';

export const WEATHER_RAIN = 'rain';
export const WEATHER_COLD = 'cold';
export const WEATHER_SUN = 'sun';
export const WEATHERS = [WEATHER_RAIN, WEATHER_COLD, WEATHER_SUN];

export const OTHER_MAX_LENGTH = 15;

/** Opción del campo `extras` de Console ("Zona techada"). Cuenta como apto para lluvia. */
export const COVERED_AREA_EXTRA = 'techada';
// "Zona techada" en Extras es, en el catálogo, una zona exterior cubierta.
const COVERED_AREA_PROTECTION = 'coveredOutdoor';

// El orden es el de los chips del formulario; cada clima enseña solo las que lo cubren.
export const PROTECTIONS = [
  { key: 'indoor', covers: [WEATHER_RAIN, WEATHER_COLD, WEATHER_SUN] },
  { key: 'indoorClimatized', covers: [WEATHER_COLD] },
  { key: 'coveredOutdoor', covers: [WEATHER_RAIN, WEATHER_COLD, WEATHER_SUN] },
  { key: 'tent', covers: [WEATHER_RAIN, WEATHER_SUN] },
  { key: 'awning', covers: [WEATHER_RAIN, WEATHER_SUN] },
  { key: 'pergola', covers: [WEATHER_RAIN, WEATHER_SUN] },
  { key: 'parasols', covers: [WEATHER_SUN] },
  { key: 'naturalShade', covers: [WEATHER_SUN] },
  { key: 'heater', covers: [WEATHER_COLD] },
  { key: 'fireplace', covers: [WEATHER_COLD] },
  { key: 'blankets', covers: [WEATHER_COLD] },
  { key: 'windbreak', covers: [WEATHER_COLD] },
];

export const protectionsForWeather = weather =>
  PROTECTIONS.filter(p => p.covers.includes(weather)).map(p => p.key);

const knownProtections = (protections, weather) =>
  Array.isArray(protections)
    ? protectionsForWeather(weather).filter(key => protections.includes(key))
    : [];

const trimOther = other => (typeof other === 'string' ? other.trim() : '');

// Un tramo guardado como '51-999' es el '+51' de siempre, no un aforo de 999 personas.
const OPEN_ENDED_GUESTS = 999;

/**
 * Capacidad del anuncio para el campo de personas a resguardo: `{ guests, max }`.
 * - `guests`: con qué se rellena el campo (el máximo del tramo; en '+51', 51).
 * - `max`: tope del campo, o null si el anuncio no tiene techo ('+51') o no se puede leer.
 * El tramo máximo es `capacityRanges` (`publicData.capacity` es el array de todos los tramos).
 */
export const listingGuestCapacity = publicData => {
  const bounds = parseGuestTierBoundsFromCapacityKey(resolveSavedCapacity(publicData));
  if (!bounds) {
    return { guests: null, max: null };
  }
  const isOpenEnded = !Number.isFinite(bounds.max) || bounds.max >= OPEN_ENDED_GUESTS;
  return isOpenEnded ? { guests: bounds.min, max: null } : { guests: bounds.max, max: bounds.max };
};

const otherTextOf = ({ otherChecked, other } = {}) =>
  otherChecked ? trimOther(other).slice(0, OTHER_MAX_LENGTH) : '';

/** ¿Tiene el clima alguna opción elegida? Mira la lista compartida y su "Otro". */
export const hasWeatherOption = (blockValues, weather) =>
  knownProtections(blockValues?.protections, weather).length > 0 ||
  !!blockValues?.[weather]?.otherChecked;

/**
 * Valores iniciales del bloque: `{ weathers, protections, rain, cold, sun }`. `weathers` son los
 * climas encendidos, `protections` la lista compartida (la unión de lo guardado en cada clima)
 * y cada clima es `{ capacity, otherChecked, other }`. La capacidad sale con la del anuncio si
 * no hay una guardada. Un anuncio que nunca ha declarado nada y tiene "Zona techada" en Extras
 * sale con Lluvia encendida y "Zona exterior cubierta" elegida: el anfitrión solo lo confirma.
 */
export const weatherProtectionFormValues = (publicData, defaultCapacity) => {
  const saved = publicData?.weatherProtection;
  const hasCoveredArea =
    Array.isArray(publicData?.extras) && publicData.extras.includes(COVERED_AREA_EXTRA);
  const prefillRain = !saved && hasCoveredArea;
  const savedProtections = WEATHERS.flatMap(w => saved?.[w]?.protections || []);
  return WEATHERS.reduce(
    (acc, weather) => {
      const savedWeather = saved?.[weather];
      return {
        ...acc,
        [weather]: {
          capacity: savedWeather?.capacity || defaultCapacity || null,
          otherChecked: !!savedWeather?.other,
          other: savedWeather?.other || '',
        },
      };
    },
    {
      weathers: prefillRain ? [WEATHER_RAIN] : WEATHERS.filter(w => !!saved?.[w]),
      protections: prefillRain
        ? [COVERED_AREA_PROTECTION]
        : PROTECTIONS.map(p => p.key).filter(key => savedProtections.includes(key)),
    }
  );
};

/**
 * Lo que el formulario guarda en `publicData`: solo los climas encendidos, cada uno con las
 * opciones de la lista compartida que lo cubren. Sin ninguno, el detalle queda a null (no un
 * objeto vacío). El texto de "Otro" solo cuenta con su chip marcado.
 */
export const weatherProtectionPublicData = (formValues, defaultCapacity) => {
  const on = WEATHERS.filter(
    w => Array.isArray(formValues?.weathers) && formValues.weathers.includes(w)
  );
  const weatherProtection = on.length
    ? on.reduce((acc, weather) => {
        const values = formValues[weather] || {};
        const otherText = otherTextOf(values);
        return {
          ...acc,
          [weather]: {
            protections: knownProtections(formValues.protections, weather),
            capacity: Number(values.capacity) || defaultCapacity || null,
            ...(otherText ? { other: otherText } : {}),
          },
        };
      }, {})
    : null;
  return { weatherProtection, weatherProtectedFor: on };
};

/** ¿Protege de este clima? Lee el campo plano, que es el mismo que filtra Flex. */
export const isProtectedFrom = (publicData, weather) =>
  Array.isArray(publicData?.weatherProtectedFor) &&
  publicData.weatherProtectedFor.includes(weather);
